import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import { SignUpScreenProps } from '@packages/shared/core/quiz/entities';
import GoogleLogin from '@packages/shared/core/common/components/common/GoogleLogin';
import { config } from '@packages/shared/config';
import CircularProgress from '@packages/shared/core/common/components/common/CircularProgress';
import { isEmailValid } from '@packages/shared/core/common/utils/string';
import Header from '@packages/shared/core/common/components/layout/Header';
import ButtonBack from '@packages/shared/core/common/components/common/ButtonBack';
import Button from '@packages/shared/core/common/components/common/Button';
import EmailFieldWithSuggestion from '@packages/shared/core/common/components/common/EmailFieldWithSuggestion';
import { useSystem } from '@packages/shared/core/common/hooks';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import Checkbox from '@packages/shared/core/common/components/common/Checkbox';
import { prepareHtml } from '@packages/shared/core/common/utils/prepareHtml';
import styles from './SignUpScreen.module.scss';

const SignUpScreen = ({
  onBack,
  goNext,
  content,
  signUpWithGoogle,
  signUpByGoogleStatus,
  errorMessage,
}: SignUpScreenProps) => {
  const { isInAppBrowser } = useSystem();

  const [isConsentChecked, setIsConsentChecked] = useState(false);

  const handleConsentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(event.target.checked);
  };

  const emailRef = useRef<HTMLInputElement>(null);

  const { getFieldProps, handleSubmit, errors, touched, isSubmitting, setFieldValue, dirty } =
    useFormik({
      initialValues: {
        email: '',
      },
      onSubmit: async (data, formikHelpers) => {
        await goNext(data, formikHelpers);
      },
      validate: async ({ email }) => {
        const err = {};

        if (!email) {
          Object.assign(err, { email: 'Email is required' });
        }

        if (!isEmailValid(email)) {
          Object.assign(err, { email: 'Invalid email' });
        }

        return err;
      },
    });

  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  const emailError = touched.email && !!errors.email ? errors.email : '';

  const submitDisabled = isSubmitting || !isConsentChecked || !dirty;

  return (
    <>
      <Header className={styles.header} menuClassName={styles.menu}>
        <ButtonBack className={styles.buttonBack} secondary onClick={onBack} />
      </Header>

      <div className={styles.contentWrapper}>
        <h2 className={styles.title}>{content.title}</h2>

        <h3 className={styles.subtitle}>{content.subtitle}</h3>

        <div className={styles.content}>
          {!isInAppBrowser && (
            <>
              <div className={styles.googleWrapper}>
                <GoogleLogin
                  clientId={config.googleClientId}
                  onSuccess={signUpWithGoogle}
                  buttonText="Sign up with Google"
                  className={styles.google}
                  disabled={signUpByGoogleStatus === LoadingStatuses.PENDING}
                />

                {signUpByGoogleStatus === LoadingStatuses.FAILED && (
                  <span className={styles.error}>{errorMessage}</span>
                )}
              </div>

              {signUpByGoogleStatus === LoadingStatuses.PENDING && <CircularProgress />}

              <div className={styles.divider}>
                <span className={styles.dividerLabel}>or</span>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className={styles.container}>
            <EmailFieldWithSuggestion
              id="email"
              ref={emailRef}
              {...getFieldProps('email')}
              error={emailError}
              label="Enter your email:"
              wrapperClass={styles.inputWrapper}
              placeholder="example@mail.com"
              onSuggestion={(suggest) => setFieldValue('email', suggest)}
              data-testid={DataTestId.EMAIL_FIELD}
            />

            <Checkbox
              dataTestId={DataTestId.CONSENT_CHECKBOX}
              name="checkbox"
              checked={isConsentChecked}
              className={styles.checkbox}
              onChange={handleConsentChange}
            >
              <p
                className={styles.consent}
                dangerouslySetInnerHTML={prepareHtml(
                  'I have read, understand and agree to the <a href="https://asknebula.com/app/terms" style="color:#8E8CF0;">Terms of Use</a> and <a href="https://asknebula.com/app/privacy" style="color:#8E8CF0;">Privacy Policy</a>. See <a href="https://www.asknebula.com/app/full-disclaimer" style="color:#8E8CF0;">Full Disclaimer</a> about the service.',
                )}
              />
            </Checkbox>

            <Button
              wrapperClassName={styles.buttonWrapper}
              type="submit"
              disabled={submitDisabled}
              className={styles.button}
              loading={isSubmitting}
              data-testid={DataTestId.SUBMIT_BUTTON}
            >
              Continue
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default SignUpScreen;
